import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { Loader2, AlertCircle, ArrowLeft, Home, Bed, Maximize2, Compass, Calendar, Phone } from "lucide-react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export default function ApartmentDetail({ id }: { id: string }) {
  const [, navigate] = useLocation();
  const listingId = parseInt(id, 10);
  const [inquirerInfo, setInquirerInfo] = useState({ name: "", email: "", phone: "" });
  const [contactResult, setContactResult] = useState<{ name: string; phone: string; email: string } | null>(null);

  const { data: listing, isLoading, error } = trpc.apartments.getPublic.useQuery(
    { id: listingId },
    { enabled: !isNaN(listingId) }
  );
  const requestContactMutation = trpc.apartments.requestContactInfo.useMutation();

  const handleRequestContact = async () => {
    if (!inquirerInfo.name || !inquirerInfo.email || !inquirerInfo.phone) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      const result = await requestContactMutation.mutateAsync({
        listingId,
        inquirerName: inquirerInfo.name,
        inquirerEmail: inquirerInfo.email,
        inquirerPhone: inquirerInfo.phone,
      });
      setContactResult({ name: result.contactName ?? "", phone: result.contactPhone ?? "", email: result.contactEmail ?? "" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to retrieve contact info");
    }
  };

  if (isNaN(listingId)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Invalid Listing</h2>
          <Button onClick={() => navigate("/apartments")} className="mt-4 bg-blue-600 hover:bg-blue-700">Back to Marketplace</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Nav */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/apartments")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Marketplace
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <Home className="w-4 h-4" />
            Home
          </Button>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {isLoading ? (
            <div className="flex justify-center items-center py-24">
              <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
            </div>
          ) : error || !listing ? (
            <Card className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Listing Not Found</h2>
              <p className="text-slate-600 mb-4">This listing may have been removed or is no longer available.</p>
              <Button onClick={() => navigate("/apartments")} className="bg-blue-600 hover:bg-blue-700">Browse All Listings</Button>
            </Card>
          ) : (
            <>
              {/* Photo Gallery */}
              {listing.photos && listing.photos.length > 0 && (
                <div className="mb-8 relative">
                  <Carousel opts={{ loop: true }} className="w-full">
                    <CarouselContent>
                      {listing.photos.map((photo: string, i: number) => (
                        <CarouselItem key={i}>
                          <div className="w-full h-72 md:h-96 bg-slate-200 overflow-hidden rounded-xl">
                            <img src={photo} alt={`Apartment photo ${i + 1}`} className="w-full h-full object-cover" />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {listing.photos.length > 1 && (
                      <>
                        <CarouselPrevious className="left-3" />
                        <CarouselNext className="right-3" />
                      </>
                    )}
                  </Carousel>
                  <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded">
                    {listing.photos.length} photo{listing.photos.length !== 1 ? "s" : ""}
                  </div>
                </div>
              )}
              {(!listing.photos || listing.photos.length === 0) && (
                <div className="w-full h-48 bg-slate-100 rounded-xl flex items-center justify-center mb-8">
                  <Home className="w-16 h-16 text-slate-300" />
                </div>
              )}

              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                      For {listing.listingType}
                    </span>
                    <span className="text-slate-500 text-sm">
                      {formatDistanceToNow(new Date(listing.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900">
                    {listing.bhk} BHK {listing.apartmentType.charAt(0).toUpperCase() + listing.apartmentType.slice(1)} — Flat {listing.flatNumber}
                  </h1>
                  <p className="text-2xl font-semibold text-blue-700 mt-1">
                    ₹{listing.price.toLocaleString("en-IN")}
                    {listing.listingType === "rent" && <span className="text-base font-normal text-slate-500">/month</span>}
                  </p>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700 px-8 shrink-0">
                      <Phone className="w-4 h-4 mr-2" />
                      View Contact Info
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Request Contact Information</DialogTitle>
                    </DialogHeader>
                    {contactResult ? (
                      <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="font-semibold text-green-900 mb-2">Contact Details</p>
                          <p className="text-green-800"><span className="font-medium">Name:</span> {contactResult.name}</p>
                          <p className="text-green-800"><span className="font-medium">Phone:</span> {contactResult.phone}</p>
                          <p className="text-green-800"><span className="font-medium">Email:</span> {contactResult.email}</p>
                        </div>
                        <p className="text-sm text-slate-600">Please reach out directly to the resident.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-blue-800">
                            Please provide your information so we can verify you're a genuine inquirer.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
                          <Input placeholder="Full Name" value={inquirerInfo.name} onChange={(e) => setInquirerInfo({ ...inquirerInfo, name: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                          <Input type="email" placeholder="your@email.com" value={inquirerInfo.email} onChange={(e) => setInquirerInfo({ ...inquirerInfo, email: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                          <Input type="tel" placeholder="+91 XXXXX XXXXX" value={inquirerInfo.phone} onChange={(e) => setInquirerInfo({ ...inquirerInfo, phone: e.target.value })} />
                        </div>
                        <Button onClick={handleRequestContact} disabled={requestContactMutation.isPending} className="w-full bg-blue-600 hover:bg-blue-700">
                          {requestContactMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</> : "Get Contact Information"}
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>

              {/* Property Details */}
              <Card className="p-6 mb-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Property Details</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Bed className="w-4 h-4" />
                      BHK
                    </div>
                    <p className="font-semibold text-slate-900">{listing.bhk} BHK</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Maximize2 className="w-4 h-4" />
                      Area
                    </div>
                    <p className="font-semibold text-slate-900">{listing.areaInSqFt} sq ft</p>
                  </div>
                  {listing.floor && (
                    <div className="flex flex-col gap-1">
                      <p className="text-slate-500 text-sm">Floor</p>
                      <p className="font-semibold text-slate-900">{listing.floor}</p>
                    </div>
                  )}
                  {listing.facing && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <Compass className="w-4 h-4" />
                        Facing
                      </div>
                      <p className="font-semibold text-slate-900">{listing.facing}</p>
                    </div>
                  )}
                  {listing.availabilityDate && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        Available
                      </div>
                      <p className="font-semibold text-slate-900">
                        {new Date(listing.availabilityDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <p className="text-slate-500 text-sm">Type</p>
                    <p className="font-semibold text-slate-900 capitalize">{listing.apartmentType}</p>
                  </div>
                </div>
              </Card>

              {/* Amenities */}
              {listing.amenities && listing.amenities.length > 0 && (
                <Card className="p-6 mb-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">Amenities</h2>
                  <div className="flex flex-wrap gap-2">
                    {listing.amenities.map((amenity: string) => (
                      <span key={amenity} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </Card>
              )}

              {/* Description */}
              {listing.description && (
                <Card className="p-6 mb-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">Description</h2>
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{listing.description}</p>
                </Card>
              )}

              {/* CTA Footer */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-slate-900">Interested in this property?</p>
                  <p className="text-slate-600 text-sm">Contact the owner directly to arrange a visit.</p>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Phone className="w-4 h-4 mr-2" />
                      Get Contact Info
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Request Contact Information</DialogTitle>
                    </DialogHeader>
                    {contactResult ? (
                      <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="font-semibold text-green-900 mb-2">Contact Details</p>
                          <p className="text-green-800"><span className="font-medium">Name:</span> {contactResult.name}</p>
                          <p className="text-green-800"><span className="font-medium">Phone:</span> {contactResult.phone}</p>
                          <p className="text-green-800"><span className="font-medium">Email:</span> {contactResult.email}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
                          <Input placeholder="Full Name" value={inquirerInfo.name} onChange={(e) => setInquirerInfo({ ...inquirerInfo, name: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                          <Input type="email" placeholder="your@email.com" value={inquirerInfo.email} onChange={(e) => setInquirerInfo({ ...inquirerInfo, email: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                          <Input type="tel" placeholder="+91 XXXXX XXXXX" value={inquirerInfo.phone} onChange={(e) => setInquirerInfo({ ...inquirerInfo, phone: e.target.value })} />
                        </div>
                        <Button onClick={handleRequestContact} disabled={requestContactMutation.isPending} className="w-full bg-blue-600 hover:bg-blue-700">
                          {requestContactMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</> : "Get Contact Information"}
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
